import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getProviderCategories, applyPartner, verifyPartnerPayment } from '@/lib/api';
import { toast } from 'sonner';
import { Store, CheckCircle2, ArrowRight, BadgeCheck, Zap, Users } from 'lucide-react';

const EMPTY = { name: '', phone: '', email: '', category: '', experience: '', city: '', state: '', service_area: '' };
const PREMIUM = new Set(['real-estate-developers', 'vastu-experts', 'interior-designers', 'architects', 'solar-installation', 'home-loan', 'property-valuation']);
const feeFor = (slug) => (slug ? (PREMIUM.has(slug) ? 1001 : 101) : null);

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const BecomeAPartnerPage = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [sp, setSp] = useSearchParams();

  useEffect(() => { getProviderCategories().then(setCategories).catch(() => {}); }, []);
  useEffect(() => {
    if (sp.get('payment') === 'failed') {
      toast.error('Payment was not completed. Please try again.');
      setSp({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const fee = feeFor(form.category);

  const openCheckout = (res) => {
    const c = res.checkout;
    const options = {
      key: c.key_id,
      order_id: c.order_id,
      amount: c.amount,
      currency: c.currency,
      name: 'Bharat Estate',
      description: `Listing fee — ${res.category_label}`,
      prefill: { name: c.name, contact: c.contact },
      theme: { color: '#D4AF37' },
      handler: async (rzp) => {
        try {
          const v = await verifyPartnerPayment({
            provider_id: res.provider_id,
            razorpay_order_id: rzp.razorpay_order_id,
            razorpay_payment_id: rzp.razorpay_payment_id,
            razorpay_signature: rzp.razorpay_signature,
          });
          setDone({ category: v.category || res.category, category_label: v.category_label || res.category_label });
          toast.success('Payment successful! 🎉');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
          toast.error(err.message || 'Payment verification failed. Please contact support.');
        } finally {
          setSubmitting(false);
        }
      },
      modal: { ondismiss: () => { setSubmitting(false); toast('Payment cancelled'); } },
    };
    const rz = new window.Razorpay(options);
    rz.on('payment.failed', () => { setSubmitting(false); toast.error('Payment failed. Please try again.'); });
    rz.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Please enter your name');
    if (!/^(\+?\d{1,3}[-\s]?)?\d{10}$/.test(form.phone.replace(/\s/g, ''))) return toast.error('Please enter a valid 10-digit phone number');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error('Please enter a valid email address');
    if (!form.category) return toast.error('Please select your service category');
    if (!form.city.trim()) return toast.error('Please enter your city');
    setSubmitting(true);
    try {
      const res = await applyPartner(form);
      if (res.requires_payment && res.checkout?.order_id) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error('Could not load the payment window. Please try again.'); setSubmitting(false); return; }
        openCheckout(res);
        return;
      }
      setDone(res);
      toast.success('You are now listed! 🎉');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSubmitting(false);
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4 py-20">
        <Seo title="Thanks for registering — Bharat Estate Partners" path="/become-a-partner" />
        <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-lg text-center" data-testid="partner-success">
          <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
          <h1 className="text-2xl font-bold text-[#0F172A]" data-testid="partner-success-title">You're live! 🎉</h1>
          <p className="text-slate-600 mt-3">Your payment is confirmed and your <span className="font-semibold">{done.category_label || categories.find((c) => c.slug === done.category)?.label || 'listing'}</span> listing is now live on the Bharat Estate website and app for everyone.</p>
          <p className="text-slate-500 text-sm mt-2">Customers can now find and contact you directly. Log in with this email anytime to edit your details.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
            <Link to="/partner"><Button className="rounded-full bg-[#0F172A] text-white w-full sm:w-auto" data-testid="partner-manage-listing">Manage my listing <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
            <Button variant="outline" className="rounded-full" onClick={() => { setDone(null); setForm(EMPTY); }}>List another service</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-20">
      <Seo title="Become a Partner — List your business free on Bharat Estate" description="Plumbers, electricians, interior designers, Vastu experts and more — list your business free and get discovered by property owners and buyers across India." path="/become-a-partner" />
      <div className="bg-[#0F172A] text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 text-center">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold mb-3"><Store className="h-4 w-4" /> Become a Partner</span>
          <h1 className="text-3xl md:text-5xl font-extrabold">Grow your business with Bharat Estate</h1>
          <p className="text-white/70 mt-3 max-w-2xl mx-auto">List your service and get discovered by thousands of property owners, buyers and tenants. Your listing goes live instantly after a one-time fee.</p>
          <div className="flex flex-wrap justify-center gap-6 mt-7 text-sm">
            <span className="inline-flex items-center gap-2 text-white/80"><Zap className="h-4 w-4 text-[#D4AF37]" /> Instant go-live</span>
            <span className="inline-flex items-center gap-2 text-white/80"><Users className="h-4 w-4 text-[#D4AF37]" /> Direct customer calls</span>
            <span className="inline-flex items-center gap-2 text-white/80"><BadgeCheck className="h-4 w-4 text-[#D4AF37]" /> From just ₹101</span>
          </div>
          <div className="mt-7 text-sm text-white/70">
            Already listed?{' '}
            <Link to="/partner" className="text-[#D4AF37] font-semibold underline underline-offset-4 hover:text-[#e0bf48]" data-testid="partner-already-listed">
              Log in to edit your details
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-5 sm:px-8 -mt-8">
        <div className="bg-[#FEF9EC] border border-[#EBD9A3] rounded-xl px-5 py-3 mb-4 text-center text-sm text-[#7A611B]" data-testid="partner-edit-note">
          Already have a listing? <Link to="/partner" className="font-semibold text-[#B5952F] underline underline-offset-2" data-testid="partner-edit-link">Log in with the same email to edit it</Link> — no need to register again.
        </div>
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="partner-form">
          <h2 className="text-lg font-bold text-[#0F172A]">Your details</h2>

          <div>
            <Label htmlFor="p-name">Full name / Business name *</Label>
            <Input id="p-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g., Ravi Electricals" className="mt-1" data-testid="partner-name" />
          </div>
          <div>
            <Label htmlFor="p-phone">Phone number *</Label>
            <Input id="p-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="e.g., +91 98765 43210" className="mt-1" data-testid="partner-phone" />
            <p className="text-xs text-muted-foreground mt-1">Customers will call / WhatsApp you on this number.</p>
          </div>
          <div>
            <Label htmlFor="p-email">Email address *</Label>
            <Input id="p-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="e.g., you@example.com" className="mt-1" data-testid="partner-email" />
            <p className="text-xs text-muted-foreground mt-1">We'll email your receipt here. Sign in with this email to manage your listing.</p>
          </div>
          <div>
            <Label>Service category *</Label>
            <Select value={form.category} onValueChange={(v) => set('category', v)}>
              <SelectTrigger className="mt-1" data-testid="partner-category"><SelectValue placeholder="Select what you offer (e.g., Plumber)" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {fee && <p className="text-xs mt-1.5 font-semibold text-[#B5952F]" data-testid="partner-fee">One-time listing fee: ₹{fee}</p>}
          </div>
          <div>
            <Label htmlFor="p-exp">Experience</Label>
            <Input id="p-exp" value={form.experience} onChange={(e) => set('experience', e.target.value)} placeholder="e.g., 8 years" className="mt-1" data-testid="partner-experience" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="p-city">City *</Label>
              <Input id="p-city" value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="e.g., Pune" className="mt-1" data-testid="partner-city" />
            </div>
            <div>
              <Label htmlFor="p-state">State</Label>
              <Input id="p-state" value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="e.g., Maharashtra" className="mt-1" data-testid="partner-state" />
            </div>
          </div>
          <div>
            <Label htmlFor="p-area">Area / locality <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="p-area" value={form.service_area} onChange={(e) => set('service_area', e.target.value)} placeholder="e.g., Baner, Wakad" className="mt-1" data-testid="partner-area" />
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-12 rounded-full bg-[#D4AF37] text-[#0F172A] font-bold hover:bg-[#e0bf48]" data-testid="partner-submit">
            {submitting ? 'Processing…' : (fee ? `Pay ₹${fee} & list my business` : 'List my business')}
          </Button>
          <p className="text-xs text-center text-muted-foreground">Secure payment via Razorpay. Your listing goes live automatically once payment succeeds.</p>
        </form>
      </div>
    </div>
  );
};

export default BecomeAPartnerPage;
